import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { tone, fmtQty } from './ageingUtils';

const PAGE = 300;
const SORTS = {
  oldest: (a, b) => (b.oldest_bucket - a.oldest_bucket)
    || ((b.cells[b.oldest_bucket]?.qty || 0) - (a.cells[a.oldest_bucket]?.qty || 0)),
  design: () => 0,
  qty: (a, b) => b.total_qty - a.total_qty,
};

const leadCols = (storage) => (storage === 'greige'
  ? [['design_name', 'Design'], ['shade', 'Colour'], ['unit', 'Unit']]
  : [['design_name', 'Design'], ['shade', 'Shade'], ['size', 'Size'], ['sku_code', 'SKU']]);

const QtyCell = ({ cell, idx, n, showCount, onClick, testid }) => (
  <td className="px-2 py-1.5 text-right border-l border-stone-100">
    {cell.count > 0 ? (
      <button type="button" onClick={onClick} data-testid={testid}
        className={`w-full rounded px-2 py-1 text-right tabular-nums font-semibold ${tone(idx, n).cell} hover:ring-2 hover:ring-stone-900/20 transition-shadow`}>
        {fmtQty(cell.qty)}
        {showCount && <span className="block text-[10px] font-normal opacity-70">{cell.count} {cell.count === 1 ? 'bundle' : 'bdl'}</span>}
      </button>
    ) : <span className="text-stone-300 pr-2">·</span>}
  </td>
);

export const AgeingSummaryTable = ({ report, onDrill }) => {
  const [sort, setSort] = useState('oldest');
  const [shown, setShown] = useState(PAGE);
  const rows = useMemo(() => [...report.rows].sort(SORTS[sort]), [report.rows, sort]);
  const n = report.buckets.length;
  const lead = leadCols(report.storage);
  const showCount = report.storage !== 'fg';
  const name = (r, k) => r[k] || (k === 'design_name' ? '(no design)' : '—');

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="ageing-summary">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-stone-200">
        <p className="text-sm text-stone-600" data-testid="ageing-summary-count">{rows.length.toLocaleString('en-IN')} {rows.length === 1 ? 'row' : 'rows'} · click any number to see the items</p>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-48 h-9 bg-white" data-testid="ageing-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="oldest">Oldest stock first</SelectItem>
            <SelectItem value="design">Design A–Z</SelectItem>
            <SelectItem value="qty">Highest quantity</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-auto max-h-[65vh]">
        <table className="w-full text-sm border-collapse" data-testid="ageing-summary-table">
          <thead className="sticky top-0 z-10 bg-stone-900 text-white">
            <tr>
              {lead.map(([k, l]) => <th key={k} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{l}</th>)}
              {report.buckets.map((b) => <th key={b.index} className="px-2 py-2 text-right font-semibold whitespace-nowrap">{b.label}</th>)}
              {report.has_undated && <th className="px-2 py-2 text-right font-semibold">No date</th>}
              <th className="px-3 py-2 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, shown).map((r, ri) => (
              <tr key={ri} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`ageing-row-${ri}`}>
                {lead.map(([k]) => <td key={k} className={`px-3 py-1.5 whitespace-nowrap ${k === 'design_name' ? 'font-medium text-stone-900' : 'text-stone-600'} ${k === 'sku_code' ? 'font-mono text-xs' : ''}`}>{name(r, k)}</td>)}
                {r.cells.map((c, i) => <QtyCell key={i} cell={c} idx={i} n={n} showCount={showCount} onClick={() => onDrill(r, i)} testid={`ageing-cell-${ri}-${i}`} />)}
                {report.has_undated && <QtyCell cell={r.undated} idx={-1} n={n} showCount={showCount} onClick={() => onDrill(r, -1)} testid={`ageing-cell-${ri}-undated`} />}
                <td className="px-3 py-1.5 text-right">
                  <button type="button" onClick={() => onDrill(r, null)} className="font-bold tabular-nums text-stone-900 hover:underline" data-testid={`ageing-row-total-${ri}`}>{fmtQty(r.total_qty)}</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-stone-100 font-bold">
            {report.totals.map((t) => (
              <tr key={t.unit} className="border-t-2 border-stone-300" data-testid={`ageing-total-${t.unit}`}>
                <td colSpan={lead.length} className="px-3 py-2">Total ({t.unit === 'Mtr' ? 'metres' : 'pieces'})</td>
                {t.cells.map((c, i) => <td key={i} className="px-3 py-2 text-right tabular-nums">{fmtQty(c.qty)}</td>)}
                {report.has_undated && <td className="px-3 py-2 text-right tabular-nums">{fmtQty(t.undated.qty)}</td>}
                <td className="px-3 py-2 text-right tabular-nums">{fmtQty(t.total_qty)}</td>
              </tr>
            ))}
          </tfoot>
        </table>
        {rows.length === 0 && <p className="py-16 text-center text-stone-500" data-testid="ageing-summary-empty">No stock found</p>}
      </div>
      {shown < rows.length && (
        <div className="p-3 text-center border-t border-stone-200">
          <Button variant="outline" onClick={() => setShown((s) => s + PAGE)} data-testid="ageing-show-more-btn">Show {Math.min(PAGE, rows.length - shown)} more of {rows.length - shown}</Button>
        </div>
      )}
    </div>
  );
};
